import type { ReactElement } from "react"
import type { IconType } from "react-icons"
import {
  PiArrowRight,
  PiBoat,
  PiCar,
  PiCat,
  PiDog,
  PiMotorcycle,
  PiPackage,
  PiPawPrint,
} from "react-icons/pi"

import { Anchor } from "@/app/components/anchor"
import type { ReviewSegmentDefinition } from "@/lib/reviews/segments"

const VEHICLE_CATEGORIES = new Set(["vehicle", "boat", "motorcycle"])

const REVIEW_SEGMENT_ICONS: Record<string, IconType> = {
  "dog-transport": PiDog,
  "cat-transport": PiCat,
  "pet-transport": PiPawPrint,
  "car-transport": PiCar,
  "boat-transport": PiBoat,
  "motorcycle-transport": PiMotorcycle,
}

function isPetSegment(segment: ReviewSegmentDefinition): boolean {
  return segment.category === "pet"
}

function isVehicleSegment(segment: ReviewSegmentDefinition): boolean {
  return VEHICLE_CATEGORIES.has(segment.category)
}

export function ReviewSegmentNavigation({
  segments,
}: {
  segments: readonly ReviewSegmentDefinition[]
}): ReactElement | null {
  if (!segments.length) {
    return null
  }

  const groups = [
    {
      id: "pets",
      label: "Pets & animals",
      segments: segments.filter(isPetSegment),
    },
    {
      id: "vehicles",
      label: "Vehicles & boats",
      segments: segments.filter(isVehicleSegment),
    },
    {
      id: "other",
      label: "Other transport",
      segments: segments.filter(
        (segment) => !isPetSegment(segment) && !isVehicleSegment(segment),
      ),
    },
  ].filter((group) => group.segments.length > 0)

  // Keep these links in /reviews.md; htmlToMarkdown removes nav landmarks.
  return (
    <div
      role="group"
      aria-label="Review categories"
      className="grid min-w-0 gap-6 sm:grid-cols-2 sm:gap-8"
    >
      {groups.map((group) => (
        <div key={group.id} className="min-w-0">
          <h3 className="m-0 border-b border-blue18 pb-3 text-xs font-semibold uppercase tracking-wider text-gray7">
            {group.label}
          </h3>
          <ul className="m-0 list-none p-0">
            {group.segments.map((segment) => {
              const Icon = REVIEW_SEGMENT_ICONS[segment.slug] ?? PiPackage

              return (
                <li key={segment.slug}>
                  <Anchor
                    href={`/reviews/${segment.slug}`}
                    aria-label={`${segment.label} reviews`}
                    className="group flex min-h-[56px] items-center gap-3 rounded border-b border-blue18 px-3 py-3 text-sm font-semibold text-blue5 transition-colors hover:bg-blue16 hover:text-blue7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue7 lg:min-h-[64px]"
                  >
                    <Icon
                      className="h-5 w-5 shrink-0 text-blue7"
                      aria-hidden="true"
                    />
                    <span>{segment.label}</span>
                    <PiArrowRight
                      className="ml-auto h-4 w-4 shrink-0 text-blue7 transition-transform group-hover:translate-x-1 motion-reduce:transform-none"
                      aria-hidden="true"
                    />
                  </Anchor>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

export function ReviewSegmentServiceLink({
  segment,
}: {
  segment: ReviewSegmentDefinition
}): ReactElement {
  return (
    <section className="mx-auto my-10 w-[90%] max-w-5xl rounded-xl bg-gray3 p-6 text-blue5">
      <h2 className="font-montserrat text-2xl font-bold">
        Read {segment.label.toLowerCase()} reviews
      </h2>
      <p className="my-3">
        Explore selected customer experiences from completed bookings on
        CitizenShipper.
      </p>
      <Anchor
        href={`/reviews/${segment.slug}`}
        className="inline-flex min-h-[48px] items-center font-semibold text-blue7"
      >
        See {segment.label.toLowerCase()} reviews
      </Anchor>
    </section>
  )
}
